// src/app/core/directives/pull-to-refresh.directive.ts
import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MobileAdminRefreshService } from '../services/mobile-admin-refresh.service';

/**
 * Pull-to-Refresh Directive for mobile admin views.
 *
 * This directive enables native-like pull-to-refresh functionality
 * without requiring Ionic's ion-content wrapper.
 *
 * The spinner duration is now controlled by the MobileAdminRefreshService:
 * - Page components call startRefreshOperation() before API calls
 * - Page components call endRefreshOperation() after API calls complete
 * - When all operations complete, refreshComplete$ emits and hides the spinner
 *
 * Usage:
 * ```html
 * <main appPullToRefresh [pullEnabled]="isMobileAdminView" (refresh)="onRefresh()">
 *   <!-- content -->
 * </main>
 * ```
 */
@Directive({
  selector: '[appPullToRefresh]',
  standalone: true,
})
export class PullToRefreshDirective implements OnInit, OnDestroy {
  @Input() pullEnabled = true;
  @Input() pullThreshold = 80; // Pixels to pull before triggering refresh
  @Input() maxPull = 120; // Maximum pull distance
  @Output() refresh = new EventEmitter<void>();

  private startY = 0;
  private currentY = 0;
  private pulling = false;
  private refreshing = false;
  private indicatorEl: HTMLElement | null = null;
  private spinnerEl: HTMLElement | null = null;
  private arrowEl: HTMLElement | null = null;
  private destroy$ = new Subject<void>();
  private fallbackTimeout: ReturnType<typeof setTimeout> | null = null;

  private touchStartListener: () => void;
  private touchMoveListener: () => void;
  private touchEndListener: () => void;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone,
    private mobileAdminRefreshService: MobileAdminRefreshService
  ) {}

  ngOnInit(): void {
    this.createIndicator();
    this.setupTouchListeners();
    this.subscribeToRefreshComplete();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearFallbackTimeout();
    this.removeTouchListeners();
    this.removeIndicator();
  }

  /**
   * Subscribe to the refresh complete signal from the service.
   * This allows page components to control when the spinner hides.
   */
  private subscribeToRefreshComplete(): void {
    this.mobileAdminRefreshService.refreshComplete$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.refreshing) {

          this.clearFallbackTimeout();
          this.completeRefresh();
        }
      });
  }

  /**
   * Hoehe des aktuell sichtbaren Headers fuer die Positionierung des Indikators.
   * Im Mobile-Admin-Layout liefert --mobile-admin-header-height 48px (Telefon) bzw.
   * 56px (Tablet); im Desktop-/Tablet-Querformat ist diese Variable 0, dann auf
   * --actual-header-height (55px) ausweichen, sonst Default 56.
   */
  /**
   * Loest einen CSS-Laengenausdruck zuverlaessig in Pixel auf — auch
   * `max(...)`, `env(...)` oder `calc(...)`. parseInt() scheitert an diesen
   * (z.B. `--safe-area-inset-top: max(30px, 32px)` -> NaN), was zuvor dazu
   * fuehrte, dass der Indikator mit `top: NaNpx` ausserhalb des Viewports
   * haengen blieb und der Spinner nie sichtbar wurde.
   */
  private resolveCssPx(expr: string): number {
    const value = (expr || '').trim();
    if (!value) return 0;
    // Schnellpfad fuer reine "NNpx"-Werte (kein DOM noetig).
    const simple = /^(-?\d+(?:\.\d+)?)px$/.exec(value);
    if (simple) return parseFloat(simple[1]);
    try {
      const probe = this.renderer.createElement('div');
      this.renderer.setStyle(probe, 'position', 'absolute');
      this.renderer.setStyle(probe, 'visibility', 'hidden');
      this.renderer.setStyle(probe, 'pointerEvents', 'none');
      this.renderer.setStyle(probe, 'height', value);
      this.renderer.appendChild(document.body, probe);
      const px = probe.getBoundingClientRect().height;
      this.renderer.removeChild(document.body, probe);
      return Number.isFinite(px) ? px : 0;
    } catch {
      return 0;
    }
  }

  private getHeaderHeight(): number {
    const root = getComputedStyle(document.documentElement);
    // resolveCssPx() statt parseInt(): dieselbe Falle, die oben fuer
    // --safe-area-inset-top schon behoben ist. parseInt('calc(48px + 8px)') ist NaN,
    // `|| 0` macht daraus stillschweigend 0, und der Indikator sitzt an der falschen
    // Hoehe — ohne Fehlermeldung. Die beiden Variablen sind heute reine px-Literale
    // (zugesichert in core/layout/mobile-admin-compact.ts), aber diese Funktion darf
    // nicht davon abhaengen, dass das so bleibt.
    const mobile = this.resolveCssPx(
      root.getPropertyValue('--mobile-admin-header-height')
    );
    if (mobile > 0) return mobile;
    const actual = this.resolveCssPx(
      root.getPropertyValue('--actual-header-height')
    );
    return actual > 0 ? actual : 56;
  }

  private clearFallbackTimeout(): void {
    if (this.fallbackTimeout) {
      clearTimeout(this.fallbackTimeout);
      this.fallbackTimeout = null;
    }
  }

  private createIndicator(): void {
    // Create pull indicator element
    this.indicatorEl = this.renderer.createElement('div');
    this.renderer.addClass(this.indicatorEl, 'pull-refresh-indicator');
    this.renderer.setStyle(this.indicatorEl, 'position', 'fixed');
    this.renderer.setStyle(this.indicatorEl, 'top', '-60px'); // Start completely hidden above viewport
    this.renderer.setStyle(this.indicatorEl, 'left', '50%');
    this.renderer.setStyle(this.indicatorEl, 'transform', 'translateX(-50%)');
    this.renderer.setStyle(this.indicatorEl, 'width', '40px');
    this.renderer.setStyle(this.indicatorEl, 'height', '40px');
    this.renderer.setStyle(this.indicatorEl, 'borderRadius', '50%');
    // Use the same colors as the mobile admin header for consistency in both light and dark mode
    this.renderer.setStyle(this.indicatorEl, 'backgroundColor', 'var(--page-header-bg-gradient-start, var(--sidebar-bg, #1e2a38))');
    this.renderer.setStyle(this.indicatorEl, 'boxShadow', '0 2px 8px rgba(0,0,0,0.3)');
    this.renderer.setStyle(this.indicatorEl, 'display', 'flex');
    this.renderer.setStyle(this.indicatorEl, 'alignItems', 'center');
    this.renderer.setStyle(this.indicatorEl, 'justifyContent', 'center');
    this.renderer.setStyle(this.indicatorEl, 'zIndex', '9999');
    this.renderer.setStyle(this.indicatorEl, 'transition', 'top 0.2s ease-out, opacity 0.2s ease-out');
    this.renderer.setStyle(this.indicatorEl, 'pointerEvents', 'none');
    this.renderer.setStyle(this.indicatorEl, 'opacity', '0'); // Hidden by default
    this.renderer.setStyle(this.indicatorEl, 'visibility', 'hidden'); // Extra safety

    // Create arrow icon
    this.arrowEl = this.renderer.createElement('span');
    this.renderer.setStyle(this.arrowEl, 'fontSize', '20px');
    // Use the same text color as the mobile admin header for consistency in both light and dark mode
    this.renderer.setStyle(this.arrowEl, 'color', 'var(--page-header-text-color, var(--clr-text-100, #fff))');
    this.renderer.setStyle(this.arrowEl, 'transition', 'transform 0.2s ease-out');
    this.arrowEl.innerHTML = '&#8595;'; // Down arrow
    this.renderer.appendChild(this.indicatorEl, this.arrowEl);

    // Create spinner (hidden by default)
    this.spinnerEl = this.renderer.createElement('span');
    this.renderer.addClass(this.spinnerEl, 'pull-refresh-spinner');
    this.renderer.setStyle(this.spinnerEl, 'display', 'none');
    this.renderer.setStyle(this.spinnerEl, 'width', '20px');
    this.renderer.setStyle(this.spinnerEl, 'height', '20px');
    // Use the same text color as the mobile admin header for the spinner border
    this.renderer.setStyle(this.spinnerEl, 'border', '2px solid var(--page-header-text-color, var(--clr-text-100, #fff))');
    this.renderer.setStyle(this.spinnerEl, 'borderTopColor', 'transparent');
    this.renderer.setStyle(this.spinnerEl, 'borderRadius', '50%');
    // Use webkit prefix for better Android WebView compatibility
    this.renderer.setStyle(this.spinnerEl, '-webkit-animation', 'pull-refresh-spin 0.8s linear infinite');
    this.renderer.setStyle(this.spinnerEl, 'animation', 'pull-refresh-spin 0.8s linear infinite');
    this.renderer.appendChild(this.indicatorEl, this.spinnerEl);

    // Add keyframes for spinner animation
    this.addSpinnerStyles();

    // Append to body
    this.renderer.appendChild(document.body, this.indicatorEl);
  }

  private addSpinnerStyles(): void {
    const styleId = 'pull-refresh-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      // Include webkit prefixes for Android WebView compatibility
      style.textContent = `
        @-webkit-keyframes pull-refresh-spin {
          0% { -webkit-transform: rotate(0deg); transform: rotate(0deg); }
          100% { -webkit-transform: rotate(360deg); transform: rotate(360deg); }
        }
        @keyframes pull-refresh-spin {
          0% { -webkit-transform: rotate(0deg); transform: rotate(0deg); }
          100% { -webkit-transform: rotate(360deg); transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  private removeIndicator(): void {
    if (this.indicatorEl && this.indicatorEl.parentNode) {
      this.renderer.removeChild(document.body, this.indicatorEl);
    }
  }

  private setupTouchListeners(): void {
    this.zone.runOutsideAngular(() => {
      this.touchStartListener = this.renderer.listen(
        this.el.nativeElement,
        'touchstart',
        (e: TouchEvent) => this.onTouchStart(e)
      );

      this.touchMoveListener = this.renderer.listen(
        this.el.nativeElement,
        'touchmove',
        (e: TouchEvent) => this.onTouchMove(e)
      );

      this.touchEndListener = this.renderer.listen(
        this.el.nativeElement,
        'touchend',
        () => this.onTouchEnd()
      );
    });
  }

  private removeTouchListeners(): void {
    if (this.touchStartListener) this.touchStartListener();
    if (this.touchMoveListener) this.touchMoveListener();
    if (this.touchEndListener) this.touchEndListener();
  }

  private onTouchStart(e: TouchEvent): void {
    if (!this.pullEnabled || this.refreshing) return;

    // Check if touch originated from within a scrollable child element that is not at top
    const touchTarget = e.target as HTMLElement;
    if (this.isInsideScrollableChild(touchTarget)) {
      // Don't start pull-to-refresh if inside a scrollable child that can scroll up
      return;
    }

    // Only start pulling if at top of scroll
    if (this.el.nativeElement.scrollTop <= 0) {
      this.startY = e.touches[0].clientY;
      this.pulling = true;
    }
  }

  /**
   * Check if the element is inside a scrollable child container that is not at the top.
   * This prevents pull-to-refresh from interfering with scrolling in nested scrollable areas.
   *
   * Only blocks pull-to-refresh if the scrollable child is scrolled down (scrollTop > 0).
   * When the child is at the top, pull-to-refresh is allowed (matching native behavior).
   */
  private isInsideScrollableChild(element: HTMLElement | null): boolean {
    let current = element;

    while (current && current !== this.el.nativeElement && current !== document.body) {
      // Check if this element is scrollable vertically
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

      if (isScrollable) {
        // Check if the element has scrollable content AND is scrolled down
        const hasScrollableContent = current.scrollHeight > current.clientHeight;

        if (hasScrollableContent && current.scrollTop > 0) {
          // Scrollable child is NOT at the top - user wants to scroll up, not pull-to-refresh
          return true;
        }
      }

      current = current.parentElement;
    }

    return false;
  }

  private onTouchMove(e: TouchEvent): void {
    if (!this.pulling || this.refreshing) return;

    // Check if still at top
    if (this.el.nativeElement.scrollTop > 0) {
      this.pulling = false;
      this.resetIndicator();
      return;
    }

    this.currentY = e.touches[0].clientY;
    const pullDistance = Math.max(0, this.currentY - this.startY);

    if (pullDistance > 0) {
      // Prevent default scroll behavior when pulling
      e.preventDefault();

      // Apply resistance to pull
      const resistance = 0.5;
      const adjustedPull = Math.min(pullDistance * resistance, this.maxPull);

      this.updateIndicator(adjustedPull);
    }
  }

  private onTouchEnd(): void {
    if (!this.pulling) return;

    const pullDistance = Math.max(0, this.currentY - this.startY);
    const adjustedPull = Math.min(pullDistance * 0.5, this.maxPull);

    if (adjustedPull >= this.pullThreshold && !this.refreshing) {
      this.triggerRefresh();
    } else {
      this.resetIndicator();
    }

    this.pulling = false;
    this.startY = 0;
    this.currentY = 0;
  }

  private updateIndicator(pullDistance: number): void {
    if (!this.indicatorEl || !this.arrowEl) return;

    // Calculate safe area offset for mobile header
    const safeAreaTop = this.resolveCssPx(
      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top')
    );
    const headerHeight = this.getHeaderHeight();
    const topOffset = safeAreaTop + headerHeight + 10;

    // Make indicator visible when pulling
    this.renderer.setStyle(this.indicatorEl, 'visibility', 'visible');
    this.renderer.setStyle(this.indicatorEl, 'opacity', '1');

    // Position indicator based on pull distance using top property
    const topPosition = Math.min(pullDistance, this.maxPull) + topOffset - 40;
    this.renderer.setStyle(this.indicatorEl, 'top', `${topPosition}px`);

    // Rotate arrow when threshold is reached
    if (pullDistance >= this.pullThreshold) {
      this.renderer.setStyle(this.arrowEl, 'transform', 'rotate(180deg)');
      this.renderer.setStyle(this.arrowEl, '-webkit-transform', 'rotate(180deg)');
    } else {
      this.renderer.setStyle(this.arrowEl, 'transform', 'rotate(0deg)');
      this.renderer.setStyle(this.arrowEl, '-webkit-transform', 'rotate(0deg)');
    }
  }

  private triggerRefresh(): void {
    this.refreshing = true;

    // Show spinner, hide arrow
    if (this.arrowEl) {
      this.renderer.setStyle(this.arrowEl, 'display', 'none');
    }
    if (this.spinnerEl) {
      this.renderer.setStyle(this.spinnerEl, 'display', 'block');
    }

    // Keep indicator visible at fixed position below header
    const safeAreaTop = this.resolveCssPx(
      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top')
    );
    const headerHeight = this.getHeaderHeight();
    const topOffset = safeAreaTop + headerHeight + 20;

    if (this.indicatorEl) {
      this.renderer.setStyle(this.indicatorEl, 'visibility', 'visible');
      this.renderer.setStyle(this.indicatorEl, 'opacity', '1');
      this.renderer.setStyle(this.indicatorEl, 'top', `${topOffset}px`);
    }

    // Emit refresh event
    this.zone.run(() => {
      this.refresh.emit();
    });

    // Fallback timeout (10s) in case components don't properly signal completion
    // This is a safety net - normally the refreshComplete$ signal will hide the spinner
    this.clearFallbackTimeout();
    this.fallbackTimeout = setTimeout(() => {
      if (this.refreshing) {
        console.warn('[PullToRefresh] Fallback timeout reached - forcing refresh complete');
        this.completeRefresh();
      }
    }, 10000);
  }

  /**
   * Call this method to signal that refresh is complete.
   */
  public completeRefresh(): void {
    this.refreshing = false;
    this.resetIndicator();
  }

  private resetIndicator(): void {
    if (!this.indicatorEl || !this.arrowEl || !this.spinnerEl) return;

    // Hide indicator completely
    this.renderer.setStyle(this.indicatorEl, 'opacity', '0');
    this.renderer.setStyle(this.indicatorEl, 'top', '-60px');

    // Reset visibility after transition completes
    setTimeout(() => {
      if (this.indicatorEl) {
        this.renderer.setStyle(this.indicatorEl, 'visibility', 'hidden');
      }
    }, 200);

    // Show arrow, hide spinner
    this.renderer.setStyle(this.arrowEl, 'display', 'block');
    this.renderer.setStyle(this.arrowEl, 'transform', 'rotate(0deg)');
    this.renderer.setStyle(this.arrowEl, '-webkit-transform', 'rotate(0deg)');
    this.renderer.setStyle(this.spinnerEl, 'display', 'none');
  }
}
